import { createTheme } from "@mui/material";
import { ThemeOptions } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface Palette {
    gradients: {
      yellowPink: string;
      greenBlue: string;
      bluePink: string;
      wildGreenBoy: string;
    };
    green: {
      main: string;
    };
    pink: {
      main: string;
    };
    yellow: {
      main: string;
    };
    tan: {
      main: string;
    };
    gray: {
      main: string;
    };
    nav: {
      light: {
        menuItem: string;
        background: string;
        menuItemActive: string;
      };
      dark: {
        menuItem: string;
        background: string;
        menuItemActive: string;
      };
    };
  }

  interface PaletteOptions {
    gradients?: {
      yellowPink?: string;
      greenBlue?: string;
      bluePink?: string;
      wildGreenBoy?: string;
    };
    green: {
      main: string;
    };
    pink: {
      main: string;
    };
    yellow: {
      main: string;
    };
    tan: {
      main: string;
    };
    gray: {
      main: string;
    };
    nav: {
      light: {
        menuItem: string;
        background: string;
        menuItemActive: string;
      };
      dark: {
        menuItem: string;
        background: string;
        menuItemActive: string;
      };
    };
  }

  interface TypographyVariants {
    xl1: React.CSSProperties;
    xl2: React.CSSProperties;
    xl3: React.CSSProperties;
    textSMLight: React.CSSProperties;
    textSMMedium?: React.CSSProperties;
    textXLLight: React.CSSProperties;
    textXLMedium: React.CSSProperties;
    textXLSemiBold: React.CSSProperties;
    textXLBold: React.CSSProperties;
    textXLExtraBold: React.CSSProperties;
    displayMDLight?: React.CSSProperties;
    displayMDMedium?: React.CSSProperties;
    displayMDBold?: React.CSSProperties;
    displayXSExtraLight?: React.CSSProperties;
    displayXSLight?: React.CSSProperties;
    displayXSBold?: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    xl1?: React.CSSProperties;
    xl2?: React.CSSProperties;
    xl3?: React.CSSProperties;
    textSMLight: React.CSSProperties;
    textSMMedium?: React.CSSProperties;
    textXLLight?: React.CSSProperties;
    textXLMedium?: React.CSSProperties;
    textXLSemiBold: React.CSSProperties;
    textXLBold?: React.CSSProperties;
    textXLExtraBold: React.CSSProperties;
    displayMDLight?: React.CSSProperties;
    displayMDMedium?: React.CSSProperties;
    displayMDBold?: React.CSSProperties;
    displayXSExtraLight?: React.CSSProperties;
    displayXSLight?: React.CSSProperties;
    displayXSBold?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    xl1: true;
    xl2: true;
    xl3: true;
    textSMLight: true;
    textSMMedium: true;
    textXLLight: true;
    textXLMedium: true;
    textXLSemiBold: true;
    textXLBold: true;
    textXLExtraBold: true;
    displayMDLight?: true;
    displayMDMedium?: true;
    displayMDBold?: true;
    displayXSExtraLight?: true;
    displayXSLight?: true;
    displayXSBold?: true;
  }
}

const pxToRem = (px: number) => `${px / 16}rem`;

const themeOptions: ThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1380,
    },
  },
  shape: {
    borderRadius: 2,
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 7,
        },
      },
    },
  },
  palette: {
    mode: "light",
    primary: {
      main: "#013B49",
      500: "#006064",
    },
    secondary: {
      main: "#f50057",
    },
    green: {
      main: "#4CFF3C",
    },
    pink: {
      main: "#FF0048",
    },
    yellow: {
      main: "#EEFF41",
    },
    tan: {
      main: "#D5D0C3",
    },
    gray: {
      main: "#E0E0E0",
    },
    common: {
      white: "#ffffff",
      black: "#333333",
    },
    info: {
      main: "#3CFFE5",
    },
    gradients: {
      yellowPink: "linear-gradient(0deg, #FF00F5 -96.73%, #FFF61F 148.85%)",
      greenBlue: "linear-gradient(0deg, #0D99FF -96.73%, #4CFF3C 148.85%)",
      bluePink: "linear-gradient(90deg, #0CAED6 0%, #FF00F5 100%)",
      wildGreenBoy: "linear-gradient(0deg, #4CFF3C 0%, #FFF61F 100%)",
    },
    nav: {
      light: {
        menuItem: "#013B49",
        menuItemActive: "#f50057",
        background: "#ffffff",
      },
      dark: {
        menuItem: "#ffffff",
        menuItemActive: "#4CFF3C",
        background: "#013B49",
      },
    },
  },
  typography: {
    fontFamily: "Poppins, Arial, sans-serif",
    xl1: {
      fontSize: pxToRem(161), // 161px to rem
      textTransform: "uppercase",
      fontWeight: 700,
    },
    xl2: {
      fontSize: pxToRem(72), // 72px to rem
      textTransform: "uppercase",
      fontWeight: 700,
    },
    xl3: {
      fontSize: pxToRem(50), // 50px to rem
      textTransform: "uppercase",
      fontWeight: 700,
    },
    textSMLight: {
      fontSize: pxToRem(16),
      fontWeight: 300,
      lineHeight: 1.5,
    },
    textSMMedium: {
      fontSize: pxToRem(18),
      fontWeight: 400,
      lineHeight: 1.5,
    },
    textXLLight: {
      fontSize: pxToRem(20),
      fontWeight: 300,
      lineHeight: 1.5,
    },
    textXLMedium: {
      fontSize: pxToRem(20),
      fontWeight: 400,
      lineHeight: 1.5,
    },
    textXLSemiBold: {
      fontSize: pxToRem(20),
      fontWeight: 500,
      lineHeight: 1.5,
    },
    textXLBold: {
      fontSize: pxToRem(20),
      fontWeight: 700,
      lineHeight: 1.5,
    },
    textXLExtraBold: {
      fontSize: pxToRem(20),
      fontWeight: 700,
      lineHeight: 1.5,
    },
    displayMDLight: {
      fontSize: pxToRem(36),
      fontWeight: 300,
      lineHeight: 1.5,
    },
    displayMDMedium: {
      fontSize: pxToRem(36),
      fontWeight: 400,
      lineHeight: 1.5,
    },
    displayMDBold: {
      fontSize: pxToRem(36),
      fontWeight: 700,
      lineHeight: 1.5,
    },
    displayXSExtraLight: {
      fontSize: pxToRem(24),
      fontWeight: 200,
      lineHeight: 1.5,
    },
    displayXSLight: {
      fontSize: pxToRem(24),
      fontWeight: 300,
      lineHeight: 1.5,
    },
    displayXSBold: {
      fontSize: pxToRem(24),
      fontWeight: 600,
      lineHeight: 1.5,
    },
  },
};

export const customTheme = createTheme(themeOptions);
